import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";

import { Box, Button, TextField} from "@mui/material";
import LoadingButton from '@mui/lab/LoadingButton';
import { useMutation } from "react-query";

import { normalizeDescriptionMarkdown } from "./MarkdownText";
import { askChatGPT } from "../utils/queries/openai";
import { PreviewData, UserData } from "../utils/types";
import { fetchNewLogo, fetchNewName, fetchNewTagline, postCompany } from "../utils/queries/companies";

interface CreateProps {
  handleOnSuccess: (newId: string) => void;
  hasCompany: boolean;
  isLoadingUserData: boolean;
  isFetchingJWT: boolean;
  isGenerating: boolean;
  userData?: UserData;
  onSubmit?: (newVal?: string) => void;
  onError?: (message: string) => void;
  onRequireAuth?: () => void;
  previewData?: PreviewData;
  setPreviewData: Dispatch<SetStateAction<PreviewData>>;
};

const DiceIcon = () => (
  <Box component="svg" viewBox="0 0 24 24" aria-hidden sx={{ width: 22, height: 22 }}>
    <rect x="1.25" y="1.25" width="21.5" height="21.5" rx="3" fill="#1c1916" />
    <circle cx="7.2" cy="7.2" r="1.55" fill="#fff" />
    <circle cx="16.8" cy="7.2" r="1.55" fill="#fff" />
    <circle cx="12" cy="12" r="1.55" fill="#fff" />
    <circle cx="7.2" cy="16.8" r="1.55" fill="#fff" />
    <circle cx="16.8" cy="16.8" r="1.55" fill="#fff" />
  </Box>
);

export const Create = ({ handleOnSuccess, hasCompany, isLoadingUserData, isFetchingJWT, isGenerating, onSubmit, onError, onRequireAuth, setPreviewData, userData }: CreateProps) => {
  const [userPrompt, setUserPrompt] = useState("");

  const limitMet = useMemo(() => (!userData?.grantedProducts.some(product => product === 'venturify') && hasCompany), [hasCompany, userData?.grantedProducts])

  const reportError = (error: unknown) => {
    setPreviewData({});
    onError?.(error instanceof Error ? error.message : "Could not generate a company");
  };

  const { mutate: createCompany, isLoading } = useMutation((companyData: PreviewData) => postCompany(companyData), {
    onSuccess: (res: { company: { _id: string } }) => {
      handleOnSuccess(res.company._id);
      setUserPrompt("");
    },
    onError: reportError,
  });

  const [shouldDisableSubmit, setShouldDisableSubmit] = useState(isLoading || isGenerating || isFetchingJWT || isLoadingUserData || limitMet);

  const generateCompany = async (defaultPrompt?: string) => {
    if (defaultPrompt?.length) setUserPrompt("")

    onSubmit && onSubmit(undefined);
    setPreviewData({ id: "test_id" });

    const prompt = defaultPrompt?.length ? defaultPrompt : userPrompt;

    try {
      const name = await fetchNewName(prompt);

      setPreviewData(prevPreviewData => ({ ...prevPreviewData, name }));

      const descriptionRaw = await askChatGPT(`Business idea: ${prompt}. The company is already named "${name}". Write a description of this one company. Use markdown: one short paragraph on what it does and who it is for, then a "Key features" heading with a few bullets about its offerings. Do not suggest other names, do not list name ideas, and do not use a table.`);
      const description = normalizeDescriptionMarkdown(descriptionRaw);

      setPreviewData(prevPreviewData => ({ ...prevPreviewData, description }));

      const tagline = await fetchNewTagline({ name, description });

      setPreviewData(prevPreviewData => ({ ...prevPreviewData, tagline }));

      const slugRaw = await askChatGPT(`Company name: ${name}. Idea: ${prompt}. Reply with only a short url slug for this company: one lowercase word, or hyphenated words. No spaces, labels, or explanation.`, 1, 8);
      const slug = slugRaw.split("\n").join("").split("").filter((c: string) => {
        return (c >= "0" && c <= "9") || (c >= "a" && c <= "z") || (c >= "A" && c <= "Z") || c === "-"
      }).join("").trim();

      setPreviewData(prevPreviewData => ({ ...prevPreviewData, slug }));

      const logo = await fetchNewLogo({ name, description });

      setPreviewData(prevPreviewData => ({ ...prevPreviewData, logo: [logo] }));

      createCompany({
        name,
        description,
        logo: [logo],
        prompt,
        slug,
        tagline,
      });
    } catch (error) {
      reportError(error);
    }
  };

  useEffect(() => {
    if (!shouldDisableSubmit && (isLoading || isGenerating || isFetchingJWT || isLoadingUserData || limitMet)) setShouldDisableSubmit(true)
    else if (shouldDisableSubmit && !(isLoading || isGenerating || isFetchingJWT || isLoadingUserData || limitMet)) setShouldDisableSubmit(false);
  }, [isLoading, isGenerating, isFetchingJWT, isLoadingUserData, shouldDisableSubmit, limitMet]);

  const inputDisabled = isLoading || isGenerating || isFetchingJWT || isLoadingUserData || limitMet;

  const handleGo = () => {
    if (!userData) {
      onRequireAuth?.();
      return;
    }
    generateCompany();
  };

  const handleRoll = () => {
    if (!userData) {
      onRequireAuth?.();
      return;
    }
    generateCompany("an interesting project about something specific");
  };

  return (
    <Box sx={{ position: "relative", width: "100%", maxWidth: 680, mt: { xs: 3, sm: 4 } }}>
      <Box
        sx={{
          bgcolor: "#fff",
          border: "1px solid #e3ddd4",
          borderRadius: "8px",
          px: { xs: 1.75, sm: 2.25 },
          pt: 2.25,
          pb: 2.5,
        }}
      >
        <TextField
          disabled={inputDisabled}
          fullWidth
          placeholder="What's your idea?"
          value={userPrompt}
          onChange={(event) => setUserPrompt(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !inputDisabled) {
              event.preventDefault();
              handleGo();
            }
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "6px",
              backgroundColor: "#fff",
              fontSize: 16,
              "& fieldset": { borderColor: "#e3ddd4" },
              "&:hover fieldset": { borderColor: "#c9c2b6" },
              "&.Mui-focused fieldset": { borderColor: "#1c1916", borderWidth: "1.5px" },
            },
            "& .MuiOutlinedInput-input": {
              py: "16.5px",
              "&::placeholder": { color: "#8a8478", opacity: 1, fontWeight: 500 },
            },
          }}
        />
        <Box display="flex" justifyContent="center" alignItems="center" gap={1.5} mt={2}>
          <LoadingButton
            loading={isLoading}
            variant="contained"
            disableElevation
            disabled={(!userPrompt.length && Boolean(userData)) || isGenerating || isFetchingJWT || isLoadingUserData || limitMet}
            onClick={handleGo}
            sx={{
              bgcolor: "#1c1916",
              borderRadius: "6px",
              px: 2.5,
              height: 46,
              minWidth: 96,
              fontWeight: 700,
              fontSize: 15,
              letterSpacing: "0.06em",
              "&:hover": { bgcolor: "#000" },
              "&.Mui-disabled": { bgcolor: "#e7e2da", color: "#8a8478" },
            }}
          >
            GO
          </LoadingButton>
          <Button
            aria-label="Roll the dice"
            disabled={(shouldDisableSubmit && Boolean(userData)) || isLoadingUserData}
            onClick={handleRoll}
            sx={{
              minWidth: 48,
              width: 48,
              height: 46,
              p: 0,
              borderRadius: "6px",
              bgcolor: "#fff",
              border: "1px solid #e3ddd4",
              "&:hover": { bgcolor: "#f6f3ee", borderColor: "#c9c2b6" },
            }}
          >
            <DiceIcon />
          </Button>
        </Box>
      </Box>
    </Box>
  )
};

export default Create;